/**
 * L1J Web Client - HTML Dialog
 *
 * NPC 대화 / 퀘스트 안내 창 (화면 중앙, 기본 숨김).
 *
 * 서버는 `S_NpcTalkReturn`(opcode 119 = S_OPCODE_SHOWHTML)으로 **문장을 보내지
 * 않는다.** 보내는 것은 HTML 리소스 이름(htmlId)과 그 안에 채워 넣을 인자뿐이다:
 *
 *   writeC(S_OPCODE_SHOWHTML) + writeD(objId) + writeS(htmlId)
 *   + (인자가 있으면) writeH(1) + writeH(개수) + writeS(인자) * 개수
 *
 * 본문 HTML은 원본 클라이언트 리소스에 들어 있고 웹 클라이언트에는 없다.
 * 그래서 이 창은 **받은 그대로(htmlId + 인자)를 사람이 읽을 수 있게 보여주는
 * 최소 구현**이다. 인자에 섞여 오는 `$숫자` 이름 ID는 nameResolver로 실제
 * 이름으로 바꿔 준다 — 그것만으로도 "누구에게서 무엇을 받았는지"는 읽힌다.
 *
 * **본문이 없어도 상점에는 들어갈 수 있다.** 선택지를 서버로 돌려보내는
 * `C_NpcAction`은 원본 HTML의 링크에서 나오지만, 상점을 여는 액션 문자열은
 * 서버에 하드코딩돼 있다 (`C_NpcAction.java:222`):
 *
 *   "buy"      → S_ShopSellList (NPC가 파는 목록 = 플레이어의 **구매**)
 *   "sell"     → S_ShopBuyList  (NPC가 사주는 값 = 플레이어의 **판매**)
 *   "retrieve" → S_RetrieveList (개인 창고. `L1Dwarf` + 레벨 5 이상일 때만)
 *
 * 그래서 이 창은 본문 대신 [구매] / [판매] / [창고] 버튼을 띄우고, 돌아온 목록은
 * 자식으로 달고 있는 `ShopDialog` / `WarehouseDialog`가 받아 그린다.
 *
 * **창고만은 목록이 오지 않는 정상 응답이 있다.** 창고가 비어 있으면 서버는
 * `S_RetrieveList` 대신 htmlId `uish999`를 보내는데(`EMPTY_WAREHOUSE_HTML_ID`),
 * 그것도 결국 이 창의 `show()`로 들어온다. 그대로 대화로 그리면 빈 창고에는
 * 창이 열리지 않아 **처음 물건을 넣을 방법이 없어지므로**, `show()`가 먼저
 * 가려내 창고 창을 빈 목록으로 연다.
 *
 * 구조·스타일은 EquipmentPanel / Inventory와 같은 규약을 따른다:
 *   - Phaser Container + setScrollFactor(0) + UI 카메라 전용
 *   - 자식에게도 setScrollFactor(0)을 직접 걸어야 클릭 히트테스트가 맞는다
 *   - 닫기는 X 버튼 / [닫기] 버튼 / ESC(GameScene이 처리) 세 경로
 *
 * 대화 본문·상점·창고는 **한 컨테이너의 세 자식**이다
 * (`talkPanel` / `shopDialog` / `warehouseDialog`).
 * 목록 창을 씬 최상위에 따로 만들지 않는 이유는 GameScene이 UI 카메라에 넘길
 * 오브젝트를 씬 시작 시 한 번만 모으기 때문이다 — 나중에 만든 최상위
 * 오브젝트는 두 카메라에 이중으로 그려진다 (DragGhost와 같은 사정).
 * 곁들여 GameScene의 "포인터가 열린 창 위에 있는가" 판정도 그대로 물려받는다:
 * 그 판정은 이 컨테이너의 좌표 + `getPanelWidth/Height()`를 매번 다시 읽으므로,
 * 지금 무엇이 열려 있느냐에 따라 크기를 바꿔 돌려주면 상점 영역까지 함께 막힌다.
 */

import Phaser from 'phaser';
import { resolveMessageArg } from '../../utils/nameResolver';
import { gameState } from '../../core/GameState';
import { ConnectionManager } from '../../network/ConnectionManager';
import { ShopDialog } from './ShopDialog';
import { WarehouseDialog } from './WarehouseDialog';

/** 본문 한 줄의 높이(px). 두 줄로 접히는 인자를 고려해 넉넉히 잡는다. */
const ROW_HEIGHT = 26;

/** 화면에 한 번에 보여줄 최대 인자 수. 넘으면 잘라내고 남은 수를 알린다. */
const MAX_ROWS = 6;

/**
 * [구매] / [판매] / [창고]를 누른 뒤 목록을 기다리는 시간(ms).
 *
 * 모든 NPC가 상점·창고는 아니고, **아니면 서버는 아무것도 보내지 않는다**
 * (`C_NpcAction`은 거리가 3타일을 넘거나 `isNpcSellOnly` 등에 걸리면 그대로
 * return하고, `"retrieve"`는 `npcImpl == "L1Dwarf" && level >= 5`가 아니면
 * 분기 자체에 들어가지 않는다). 화면에 아무 일도 일어나지 않으면 사용자는 버튼이
 * 고장 난 것으로 읽으므로, 이 시간이 지나면 창 안에 그 사실을 적어 준다.
 *
 * 값은 GameScene의 `NPC_TALK_SILENCE_MS`와 같다 — 같은 성질의 침묵을
 * 같은 기준으로 판정한다. 세 버튼이 **타이머 하나를 돌려 쓰고 문구만 갈아 끼운다**
 * (`startSilenceWatch`).
 */
const SILENCE_MS = 1500;

/**
 * 창고가 비어 있을 때 서버가 목록 대신 보내는 HTML 이름.
 *
 * `C_NpcAction.java:290`의 `"retrieve"` 분기는 `getWarehouseInventory().getSize()`가
 * 0이면 `S_RetrieveList`를 만들지 않고 이 htmlId만 돌려준다
 * (「お客様名義であずかっている荷物はありませんが」). 실측으로도 그랬다 —
 * 창고지기에게 [창고]를 눌렀는데 `retrieve_list`는 0회, `show_html uish999`만 왔다.
 *
 * **그래서 이 이름은 "창고가 비었다"는 확정 신호다.** 창고 NPC가 아니거나 너무 먼
 * 경우와 명확히 구별된다 — 그 둘은 서버가 아무 것도 보내지 않는다.
 * 다만 `retrieve-char` / `retrieve-elven` / `retrieve-pledge` 분기도 같은 이름을 쓰므로
 * **htmlId만으로 창고 창을 열면 안 된다.** 우리가 방금 그 NPC에게 창고를 청했을
 * 때만 창고 신호로 읽는다 (`pendingWarehouseObjId`).
 */
const EMPTY_WAREHOUSE_HTML_ID = 'uish999';

/** 게이트웨이 show_html payload와 같은 모양 + 화면에 띄울 제목. */
export interface HtmlDialogData {
  /** 대화 상대 오브젝트 ID. */
  objId: number;
  /** 서버 HTML 템플릿 이름. 본문은 우리에게 없다. */
  htmlId: string;
  /** 템플릿에 채워 넣을 인자. `$숫자` 이름 ID가 섞여 온다. */
  args: string[];
  /** 대화 상대 이름. 모르면 생략 — 그 경우 objId를 대신 보여준다. */
  title?: string;
}

export class HtmlDialog extends Phaser.GameObjects.Container {
  private panelWidth = 380;
  /**
   * 세로 크기. 본문 6줄 + 넘침 안내 + 상태 줄 + 버튼 줄이 겹치지 않는 최소치다
   * (원래 260이었으나 버튼 줄이 생기며 늘렸다). 화면 중앙에 놓여도 위로는
   * 상단 배너(y≈66), 아래로는 퀵슬롯(y≈643)에 닿지 않는다.
   */
  private panelHeight = 320;

  /**
   * 대화 본문 쪽 화면 요소를 전부 담는 컨테이너.
   *
   * 이 컨테이너(=`this`)는 대화창과 상점창 둘을 거느리는 껍데기라 **둘 중 하나라도
   * 열려 있으면 보이는 상태**여야 한다. 그래서 "대화창이 열렸는가"는 `this.visible`이
   * 아니라 이 자식의 visible로 판단한다.
   */
  private talkPanel!: Phaser.GameObjects.Container;

  private titleText!: Phaser.GameObjects.Text;
  private htmlIdText!: Phaser.GameObjects.Text;
  private rowTexts: Phaser.GameObjects.Text[] = [];
  private emptyText!: Phaser.GameObjects.Text;
  private overflowText!: Phaser.GameObjects.Text;
  /** 상점·창고 요청의 진행 상황 / 무응답 안내. */
  private statusText!: Phaser.GameObjects.Text;

  /** 상점 목록 창. 이 컨테이너의 자식이라 UI 카메라 배정을 물려받는다. */
  private shopDialog!: ShopDialog;

  /** 개인 창고 창. 상점창과 같은 이유로 이 컨테이너의 자식이다. */
  private warehouseDialog!: WarehouseDialog;

  /** 지금 대화 중인 상대. C_NpcAction의 목적지이므로 버튼을 누를 때 필요하다. */
  private currentObjId: number | null = null;

  /** 목록 침묵 감시 타이머. 상점·창고가 함께 쓴다 (한 번에 하나만 기다린다). */
  private silenceWatch: Phaser.Time.TimerEvent | null = null;

  /**
   * 창고를 청해 놓고 아직 답을 받지 못한 상대의 objId.
   *
   * `uish999`를 "창고가 비었다"로 읽어도 되는지 가리는 유일한 근거다 (위 상수 주석).
   * 세우는 곳은 [창고] 버튼과 창고창의 거래 후 재요청(`onRetrieveRequest`) 둘이고,
   * 답이 오면(목록이든 `uish999`든) 지운다. 다른 대화가 시작되거나 창이 닫혀도 지운다 —
   * 요청의 흔적을 남겨 두면 한참 뒤 다른 맥락의 `uish999`가 창고 창을 연다.
   */
  private pendingWarehouseObjId: number | null = null;

  /** gameState 구독 해제 함수들. */
  private unsubscribers: Array<() => void> = [];

  /** 창이 닫힐 때 알린다 (GameScene이 ESC 처리 상태를 정리하는 데 쓴다). */
  public onClose: (() => void) | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);

    this.setScrollFactor(0);

    this.createUI();
    this.createShopDialog();
    this.createWarehouseDialog();
    this.subscribeListEvents();
    // 인벤토리(150)/좌측 창(160)보다 위, ESC 메뉴(200)보다는 아래.
    this.setDepth(170);
    // 껍데기뿐 아니라 대화창 자식도 닫아 둔다 — isOpen()과 getPanelWidth()가
    // 이 자식의 visible을 보므로, 켜둔 채로 두면 닫힌 창이 화면 중앙의 클릭을 먹는다.
    this.talkPanel.setVisible(false);
    this.setVisible(false);
  }

  /**
   * 지금 화면을 덮고 있는 크기.
   *
   * GameScene은 이 값으로 "포인터가 열린 창 위에 있는가"를 판정한다 (창 중심 ±절반).
   * 상점·창고창이 열려 있으면 그쪽이 더 크므로 큰 쪽을 돌려줘야 그 위의 클릭이
   * 월드로 새지 않는다. 세 창은 같은 중심(이 컨테이너의 원점)을 쓰므로 최댓값이면 충분하다.
   */
  public getPanelWidth(): number {
    return Math.max(
      this.talkPanel?.visible ? this.panelWidth : 0,
      this.shopDialog?.isOpen() ? this.shopDialog.getPanelWidth() : 0,
      this.warehouseDialog?.isOpen() ? this.warehouseDialog.getPanelWidth() : 0
    );
  }

  public getPanelHeight(): number {
    return Math.max(
      this.talkPanel?.visible ? this.panelHeight : 0,
      this.shopDialog?.isOpen() ? this.shopDialog.getPanelHeight() : 0,
      this.warehouseDialog?.isOpen() ? this.warehouseDialog.getPanelHeight() : 0
    );
  }

  /**
   * 창이 열려 있는지. ESC 처리 우선순위 판단에 쓴다.
   * 상점창·창고창도 이 창의 일부이므로 ESC 한 번으로 함께 접힌다.
   */
  public isOpen(): boolean {
    return this.talkPanel.visible || this.shopDialog.isOpen() || this.warehouseDialog.isOpen();
  }

  private createUI(): void {
    const w = this.panelWidth;
    const h = this.panelHeight;

    // 대화 본문 요소는 전부 이 안에 넣는다 (상점창과 따로 여닫기 위해).
    this.talkPanel = this.scene.add.container(0, 0);
    this.talkPanel.setScrollFactor(0);
    this.add(this.talkPanel);

    const bg = this.scene.add.rectangle(0, 0, w, h, 0x1a1a2e, 0.96);
    bg.setStrokeStyle(2, 0x4a4a6a);
    this.talkPanel.add(bg);

    const titleBar = this.scene.add.rectangle(0, -h / 2 + 15, w, 30, 0x2a2a4e);
    titleBar.setStrokeStyle(1, 0x3a3a5a);
    this.talkPanel.add(titleBar);

    this.titleText = this.scene.add.text(-w / 2 + 12, -h / 2 + 15, '대화', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    this.titleText.setOrigin(0, 0.5);
    this.talkPanel.add(this.titleText);

    const closeBtn = this.scene.add.text(w / 2 - 20, -h / 2 + 15, 'X', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ff6666',
    });
    closeBtn.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    // 걸지 않으면 카메라가 스크롤된 상태에서 히트테스트가 월드 좌표로 계산돼 빗나간다.
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setScrollFactor(0);
    closeBtn.on('pointerover', () => closeBtn.setStyle({ color: '#ffaaaa' }));
    closeBtn.on('pointerout', () => closeBtn.setStyle({ color: '#ff6666' }));
    // 닫은 뒤 씬이 이 클릭을 월드 클릭으로 오해하지 않도록 알린다
    // (Phaser는 오브젝트 핸들러를 먼저 돌리고 씬 pointerdown을 나중에 쏜다).
    // hide() 자체에는 넣지 않는다 — ESC/네트워크로 닫힐 때는 클릭이 없다.
    closeBtn.on('pointerdown', () => {
      this.hide();
      this.emit('panelclose');
    });
    this.talkPanel.add(closeBtn);

    // 템플릿 이름. 본문을 못 그리는 이상 이것이 "무슨 대화인지"를 가리키는 유일한 단서다.
    this.htmlIdText = this.scene.add.text(-w / 2 + 12, -h / 2 + 44, '', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#8899bb',
      wordWrap: { width: w - 24 },
    });
    this.htmlIdText.setOrigin(0, 0.5);
    this.talkPanel.add(this.htmlIdText);

    // 본문 줄을 미리 만들어 두고 내용만 갈아 끼운다 (EquipmentPanel과 같은 방식).
    const left = -w / 2 + 12;
    let y = -h / 2 + 74;

    for (let i = 0; i < MAX_ROWS; i++) {
      const row = this.scene.add.text(left, y, '', {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#ffffff',
        wordWrap: { width: w - 24 },
      });
      row.setOrigin(0, 0);
      row.setVisible(false);
      this.talkPanel.add(row);
      this.rowTexts.push(row);
      y += ROW_HEIGHT;
    }

    this.emptyText = this.scene.add.text(
      0,
      -10,
      '(서버가 본문을 보내지 않습니다 — 상점이라면 아래 버튼으로 들어갈 수 있습니다)',
      {
        fontSize: '11px',
        fontFamily: 'Arial, sans-serif',
        color: '#888888',
        align: 'center',
        wordWrap: { width: w - 40 },
      }
    );
    this.emptyText.setOrigin(0.5);
    this.talkPanel.add(this.emptyText);

    this.overflowText = this.scene.add.text(0, h / 2 - 74, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
    });
    this.overflowText.setOrigin(0.5);
    this.talkPanel.add(this.overflowText);

    this.statusText = this.scene.add.text(0, h / 2 - 52, '', {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffaa66',
      align: 'center',
      wordWrap: { width: w - 24 },
    });
    this.statusText.setOrigin(0.5);
    this.talkPanel.add(this.statusText);

    this.createActionButtons();
  }

  /**
   * 하단 액션 줄 — [구매] / [판매] / [창고] / [닫기].
   *
   * 원본 HTML의 링크를 재현할 수는 없지만 상점·창고 액션 문자열만은 서버에
   * 하드코딩돼 있으므로(파일 상단 주석), 그 셋만 버튼으로 세운다. **구매/판매는
   * 문구가 플레이어 관점이고 서버로 나가는 문자열은 NPC 관점이라 서로 반대다** —
   * 여기서 한 번만 뒤집는다. `"retrieve"`는 뒤집을 것이 없다.
   *
   * X는 작아서 놓치기 쉬우므로 [닫기]도 같은 줄에 둔다.
   *
   * **네 개가 한 줄에 들어가는지 계산해 둔 좌표다.** 13px Arial 기준 글자폭은
   * 한글 ≈13px / 대문자 ≈9px / 대괄호·괄호 ≈7px / 공백 ≈4px이므로
   * `[ 구매 ]`·`[ 판매 ]`·`[ 창고 ]`가 각 ≈48px, `[ 닫기 (ESC) ]`가 ≈87px이다.
   * 셋의 간격을 20px씩 두면 총 폭 48*3 + 87 + 20*3 = 291px이고, 중앙 정렬하면
   * 좌우 끝이 ∓145.5 — 패널 안쪽 여백(±178 = 380/2 - 12) 안에 33px씩 남는다.
   */
  private createActionButtons(): void {
    const h = this.panelHeight;
    const buttonY = h / 2 - 28;

    this.createActionButton(-122, buttonY, '[ 구매 ]', () => this.requestShop('buy'));
    this.createActionButton(-54, buttonY, '[ 판매 ]', () => this.requestShop('sell'));
    this.createActionButton(14, buttonY, '[ 창고 ]', () => this.requestWarehouse());
    this.createActionButton(102, buttonY, '[ 닫기 (ESC) ]', () => {
      this.hide();
      // 닫은 뒤 씬이 이 클릭을 월드 클릭으로 오해하지 않도록 알린다.
      this.emit('panelclose');
    });
  }

  private createActionButton(x: number, y: number, label: string, onClick: () => void): void {
    const button = this.scene.add.text(x, y, label, {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
    });
    button.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다.
    button.setInteractive({ useHandCursor: true });
    button.setScrollFactor(0);
    button.on('pointerover', () => button.setStyle({ color: '#ffd700' }));
    button.on('pointerout', () => button.setStyle({ color: '#ffffff' }));
    button.on('pointerdown', onClick);
    this.talkPanel.add(button);
  }

  /**
   * 상점 목록 창을 만들어 자식으로 붙인다.
   *
   * 대화창 위에 그려져야 하므로 `talkPanel` 다음에 넣는다 (컨테이너의 그리기
   * 순서는 자식 순서다). 중심을 이 컨테이너의 원점에 맞추는 것이 중요하다 —
   * GameScene의 포인터 판정이 "이 컨테이너의 좌표 ± 절반"이기 때문이다.
   */
  private createShopDialog(): void {
    this.shopDialog = new ShopDialog(this.scene, 0, 0);
    this.add(this.shopDialog);

    // 상점창이 여닫힐 때마다 껍데기의 표시 상태를 맞춘다.
    this.shopDialog.onVisibilityChange = () => this.syncRootVisibility();

    // 사용자가 상점을 닫으면 원래 대화창으로 돌아온다 — 구매↔판매를 다시 고를 수
    // 있어야 하고, 그러려면 NPC를 다시 클릭해 말을 걸어야 하는 것은 번거롭다.
    this.shopDialog.onDismiss = () => {
      if (this.currentObjId !== null) {
        this.statusText.setText('');
        this.talkPanel.setVisible(true);
      }
      this.syncRootVisibility();
    };

    // 상점창의 닫기 클릭도 월드로 새면 안 된다. GameScene은 이 컨테이너만
    // 구독하므로 자식의 신호를 그대로 올려 보낸다.
    this.shopDialog.on('panelclose', () => this.emit('panelclose'));
  }

  /**
   * 개인 창고 창을 만들어 자식으로 붙인다.
   *
   * 상점창과 같은 규약이다 — 중심을 이 컨테이너의 원점에 맞추고, 여닫힘을
   * 껍데기의 표시 상태에 반영하며, 닫기 클릭 신호를 그대로 올려 보낸다.
   * 상점창 다음에 넣어 두 목록 창이 겹칠 일이 생기더라도 순서가 흔들리지 않게 한다.
   */
  private createWarehouseDialog(): void {
    this.warehouseDialog = new WarehouseDialog(this.scene, 0, 0);
    this.add(this.warehouseDialog);

    this.warehouseDialog.onVisibilityChange = () => this.syncRootVisibility();

    // 창고창이 거래 뒤 스스로 목록을 다시 청한다. 그 응답도 창고가 비면 `uish999`로
    // 오므로, 우리가 보낸 요청과 똑같이 기억해 둬야 대화창이 그 답을 가로채지 않는다.
    this.warehouseDialog.onRetrieveRequest = (objId) => {
      this.pendingWarehouseObjId = objId;
    };

    // 창고를 닫으면 원래 대화창으로 돌아온다 (상점창과 같은 이유).
    this.warehouseDialog.onDismiss = () => {
      if (this.currentObjId !== null) {
        this.statusText.setText('');
        this.talkPanel.setVisible(true);
      }
      this.syncRootVisibility();
    };

    this.warehouseDialog.on('panelclose', () => this.emit('panelclose'));
  }

  /**
   * 상점·창고 목록 도착을 지켜본다.
   *
   * 목록이 오면 침묵 감시를 풀고 대화창을 접는다 — 창을 그리는 주체는
   * ShopDialog / WarehouseDialog 자신이므로 여기서는 "언제 대화창이 물러나야
   * 하는가"만 판단한다.
   */
  private subscribeListEvents(): void {
    const onList = (objId: number) => {
      this.clearSilenceWatch();
      if (objId === this.currentObjId) {
        this.talkPanel.setVisible(false);
      }
      this.syncRootVisibility();
    };

    this.unsubscribers.push(
      gameState.onShopSellList((data) => onList(data.objId)),
      gameState.onShopBuyList((data) => onList(data.objId)),
      gameState.onWarehouseList((data) => {
        // 목록이 왔으니 이 요청은 끝났다 — 뒤늦은 `uish999`를 창고로 읽으면 안 된다.
        this.pendingWarehouseObjId = null;
        onList(data.objId);
      })
    );

    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.clearSilenceWatch();
      this.unsubscribers.forEach((off) => off());
      this.unsubscribers = [];
    });
  }

  /**
   * 상점 목록을 요청한다 (C_NpcAction).
   *
   * @param action 서버가 아는 액션 문자열. **NPC 관점**이라 화면 문구와 반대다:
   *   `'buy'`(NPC가 산다는 뜻이 아니라 서버 분기 이름) → 플레이어의 구매 목록,
   *   `'sell'` → 플레이어의 판매 목록.
   */
  private requestShop(action: 'buy' | 'sell'): void {
    const objId = this.currentObjId;
    if (objId === null) return;

    const connection = ConnectionManager.getInstance();
    if (!connection.isConnected()) {
      this.statusText.setText('서버와 연결되어 있지 않습니다.');
      return;
    }

    connection.getPacketHandler().sendNpcAction(objId, action);
    this.statusText.setText(
      action === 'buy' ? '구매 목록을 요청했습니다...' : '판매 목록을 요청했습니다...'
    );
    this.startSilenceWatch('응답이 없습니다 — 상점이 아니거나 너무 멀리 떨어져 있습니다.');
  }

  /**
   * 개인 창고를 연다 (C_NpcAction `"retrieve"`).
   *
   * 서버는 `npcImpl == "L1Dwarf" && level >= 5`일 때만 이 분기를 타고, 조건에
   * 맞지 않으면 **에러도 없이 조용하다**. 반면 조건에 맞는데 창고가 비어 있으면
   * 목록 대신 `uish999` HTML이 오므로(위 상수 주석), 그 둘은 구별된다 —
   * 침묵은 "NPC가 아니거나 멀다"만 뜻하고, 빈 창고는 `show()`가 창고 창으로 넘긴다.
   */
  private requestWarehouse(): void {
    const objId = this.currentObjId;
    if (objId === null) return;

    const connection = ConnectionManager.getInstance();
    if (!connection.isConnected()) {
      this.statusText.setText('서버와 연결되어 있지 않습니다.');
      return;
    }

    // 응답이 `uish999`로 와도 창고 신호로 읽을 수 있도록 요청을 기억해 둔다.
    this.pendingWarehouseObjId = objId;
    connection.getPacketHandler().sendNpcAction(objId, 'retrieve');
    this.statusText.setText('창고 목록을 요청했습니다...');
    this.startSilenceWatch('응답이 없습니다 — 창고를 다루는 NPC가 아니거나 너무 멀리 떨어져 있습니다.');
  }

  /**
   * 목록이 오지 않으면 그 사실을 알린다 (해당 NPC가 아님 / 거리 초과 / 빈 창고).
   *
   * 타이머는 하나뿐이다 — 세 버튼 중 무엇을 눌러도 기다리는 대상은 하나이고,
   * 다시 누르면 이전 감시를 걷어내고 새 문구로 갈아 끼운다.
   */
  private startSilenceWatch(message: string): void {
    this.clearSilenceWatch();
    this.silenceWatch = this.scene.time.delayedCall(SILENCE_MS, () => {
      this.silenceWatch = null;
      this.statusText.setText(message);
    });
  }

  private clearSilenceWatch(): void {
    if (!this.silenceWatch) return;
    this.silenceWatch.remove(false);
    this.silenceWatch = null;
  }

  /** 껍데기는 대화창·상점창·창고창 중 하나라도 열려 있을 때만 보인다. */
  private syncRootVisibility(): void {
    this.setVisible(
      this.talkPanel.visible || this.shopDialog.isOpen() || this.warehouseDialog.isOpen()
    );
  }

  /**
   * 대화 내용을 채우고 창을 연다.
   *
   * 인자는 전부 `resolveMessageArg`를 통과시킨다 — 서버는 `$958`,
   * `$1958 $1938`, `+3 $1958 $1938 ($9)` 같은 이름 ID를 그대로 실어 보내므로
   * 치환하지 않으면 화면에 코드가 그대로 노출된다.
   *
   * **모든 `show_html`이 대화인 것은 아니다.** 빈 창고의 응답도 이 경로로 오므로
   * 본문을 갈아 끼우기 전에 먼저 가려낸다 (`consumeEmptyWarehouse`).
   */
  public show(data: HtmlDialogData): void {
    // 빈 창고의 응답이면 대화 본문을 건드리지 않고 창고 창으로 넘긴다 — 여기서
    // 아래 본문 갱신을 태우면 방금 [창고]를 누른 대화가 `uish999`로 덮여
    // 버튼도 상태 문구도 사라진다.
    if (this.consumeEmptyWarehouse(data)) return;

    // 새 대화가 시작되면 이전 상대의 상점·창고는 접는다 (되살리지 않으므로 notify=false).
    this.clearSilenceWatch();
    this.shopDialog.hide(false);
    this.warehouseDialog.hide(false);

    // 다른 대화가 시작됐으니 답을 못 받은 창고 요청은 버린다 (뒤늦게 온 `uish999`가
    // 이 대화 위로 창고 창을 열지 않게).
    this.pendingWarehouseObjId = null;

    this.currentObjId = data.objId;
    this.statusText.setText('');

    this.titleText.setText(data.title ? `대화 - ${data.title}` : `대화 - #${data.objId}`);
    this.htmlIdText.setText(`템플릿: ${data.htmlId || '(이름 없음)'}`);

    const args = (data.args ?? []).map((arg) => resolveMessageArg(arg)).filter((arg) => arg.length > 0);

    this.emptyText.setVisible(args.length === 0);

    this.rowTexts.forEach((row, index) => {
      const arg = args[index];
      if (arg === undefined) {
        row.setVisible(false);
        return;
      }
      row.setText(`· ${arg}`);
      row.setVisible(true);
    });

    const overflow = args.length - MAX_ROWS;
    this.overflowText.setText(overflow > 0 ? `... 외 ${overflow}개` : '');

    this.talkPanel.setVisible(true);
    this.syncRootVisibility();
  }

  /**
   * 빈 창고의 응답인지 가려내고, 맞으면 창고 창을 **빈 목록으로** 연다.
   *
   * 조건은 둘 다여야 한다: htmlId가 `uish999`이고, **그 상대에게 방금 창고를 청했을 것**.
   * 두 번째가 없으면 같은 htmlId를 쓰는 다른 창고 액션(`retrieve-char` /
   * `retrieve-elven` / `retrieve-pledge`)이나 무관한 대화에도 창고 창이 튀어나온다.
   *
   * 대화 본문은 손대지 않는다 — 대화창만 접어 두면 창고를 닫았을 때 `onDismiss`가
   * 방금 그 대화를 버튼째 그대로 되살린다.
   *
   * @returns 창고 응답으로 처리했으면 true (호출한 `show()`는 그대로 끝낸다).
   */
  private consumeEmptyWarehouse(data: HtmlDialogData): boolean {
    if (data.htmlId !== EMPTY_WAREHOUSE_HTML_ID) return false;
    if (this.pendingWarehouseObjId === null || this.pendingWarehouseObjId !== data.objId) {
      return false;
    }

    this.pendingWarehouseObjId = null;
    this.clearSilenceWatch();
    this.statusText.setText('');

    // 상점과 창고를 동시에 펼쳐 둘 이유는 없다. 창고는 hide하지 않는다 —
    // 거래 뒤 다시 빈 목록을 받은 경우라면 열려 있는 창을 그대로 갱신해야 한다.
    this.shopDialog.hide(false);

    this.currentObjId = data.objId;
    this.talkPanel.setVisible(false);
    this.warehouseDialog.openEmpty(data.objId);
    this.syncRootVisibility();
    return true;
  }

  /**
   * 창을 닫는다 — 대화창·상점창·창고창을 함께 접는다. 닫혀 있으면 아무 일도 하지 않는다.
   *
   * 목록 창들은 `notify=false`로 닫는다. 그러지 않으면 `onDismiss`가 대화창을
   * 되살려 ESC를 눌러도 창이 남는다.
   */
  public hide(): void {
    if (!this.isOpen()) return;

    this.clearSilenceWatch();
    this.shopDialog.hide(false);
    this.warehouseDialog.hide(false);
    this.talkPanel.setVisible(false);
    this.currentObjId = null;
    // 답을 못 받은 창고 요청도 함께 버린다 — 닫힌 뒤 도착한 `uish999`가
    // 창고 창만 덜렁 여는 일이 없게.
    this.pendingWarehouseObjId = null;
    this.statusText.setText('');

    this.setVisible(false);
    this.onClose?.();
  }

  /** Container.setVisible()은 void를 반환한다 — 체이닝을 살려 둔다. */
  public setVisible(visible: boolean): this {
    super.setVisible(visible);
    return this;
  }
}
